import * as React from "react"
import { cn } from "@/lib/utils"

interface FormLabelInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
}

const FormLabelInput = React.forwardRef<HTMLInputElement, FormLabelInputProps>(
  ({ className, label, ...props }, ref) => {
    return (
      <input
        className={cn(
          "w-full border-0 border-b-2 border-[#afb2b3] bg-white px-0 py-1.5 text-lg font-bold text-inherit focus:outline-none",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
FormLabelInput.displayName = "FormLabelInput"

interface TitleInputContainerProps {
  children: React.ReactNode
  className?: string
}

function TitleInputContainer({ children, className }: Readonly<TitleInputContainerProps>) {
  return (
    <div className={cn("mb-5 flex flex-col items-start", className)}>
      {children}
    </div>
  )
}

interface TitleDescriptionProps {
  children: React.ReactNode
  className?: string
}

function TitleDescription({ children, className }: Readonly<TitleDescriptionProps>) {
  return (
    <p className={cn("text-left text-sm font-medium text-[#858585] mt-3", className)}>
      {children}
    </p>
  )
}

interface MessageInputContainerProps {
  children: React.ReactNode
  className?: string
}

function MessageInputContainer({ children, className }: Readonly<MessageInputContainerProps>) {
  return (
    <div className={cn(
      "mb-5 flex flex-col items-start [&>.select-container]:w-full [&>.select-container]:text-left",
      className
    )}>
      {children}
    </div>
  )
}

interface FormLabelProps {
  children: React.ReactNode
  className?: string
}

function FormLabel({ children, className }: Readonly<FormLabelProps>) {
  return (
    <p className={cn("m-0 mb-2.5 p-0 text-left text-[15px] font-medium text-inherit", className)}>
      {children}
    </p>
  )
}

interface InputContainerProps {
  children: React.ReactNode
  className?: string
}

function InputContainer({ children, className }: Readonly<InputContainerProps>) {
  return (
    <div className={cn("mb-5 mt-5 flex w-full flex-col justify-start", className)}>
      {children}
    </div>
  )
}

interface FormInputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
  ({ className, ...props }, ref) => {
    return (
      <input
        className={cn(
          "h-10 w-full rounded border border-[#858585] px-2.5 text-[15px] text-inherit outline-none",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
FormInput.displayName = "FormInput"

interface FormNoteProps {
  children: React.ReactNode
  className?: string
}

function FormNote({ children, className }: Readonly<FormNoteProps>) {
  return (
    <span className={cn("mb-5 mt-1.5 text-left text-[11px] text-[#858585]", className)}>
      {children}
    </span>
  )
}

export {
  FormLabelInput,
  TitleInputContainer,
  TitleDescription,
  MessageInputContainer,
  FormLabel,
  InputContainer,
  FormInput,
  FormNote,
}
